import { createContext, FC, ReactNode, useContext } from "react";

import { ToolUIPart, UIMessage } from "ai";

import type { DonorAssistantMessageSent } from "../Chat/Chat";
import { AssistantLinkPolicy, DEFAULT_ASSISTANT_LINK_POLICY } from "../helpers/isAllowedAssistantLink";
import { getDefaultToolPartDisplay, ToolPartDisplay } from "../Thread/ToolPart/helpers";

import { useTranslate } from "@/common/hooks/useTranslate";

export type AssistantFeature = "donor-assistant" | "organization-assistant";

export type AssistantMessageSource = DonorAssistantMessageSent["source"];

export interface AssistantToolRendererProps {
  part: ToolUIPart;
  reply?: UIMessage;
  isComposerDisabled: boolean;
  onMessageSent: (source: AssistantMessageSource) => void;
}

export interface ApprovalContent {
  description: string;
  details?: ReactNode;
  noteLabel?: string;
  approveLabel?: string;
  canApprove?: boolean;
  noteValue?: string;
}

export interface AssistantConfig {
  feature: AssistantFeature;
  linkPolicy: AssistantLinkPolicy;
  getToolPartDisplay: (part: ToolUIPart) => ToolPartDisplay;
  toolRenderers: Partial<Record<string, FC<AssistantToolRendererProps>>>;
  useApprovalContent: (part: ToolUIPart, messages: UIMessage[]) => ApprovalContent;
}

const useDefaultApprovalContent = (): ApprovalContent => {
  const { t } = useTranslate();
  return { description: t("dashboard", "assistant.approval.defaultDescription") };
};

export const DEFAULT_ASSISTANT_CONFIG: AssistantConfig = {
  feature: "donor-assistant",
  linkPolicy: DEFAULT_ASSISTANT_LINK_POLICY,
  getToolPartDisplay: getDefaultToolPartDisplay,
  toolRenderers: {},
  useApprovalContent: useDefaultApprovalContent,
};

const AssistantConfigContext = createContext<AssistantConfig>(DEFAULT_ASSISTANT_CONFIG);

interface AssistantConfigProviderProps {
  value: AssistantConfig;
  children: ReactNode;
}

export const AssistantConfigProvider: FC<AssistantConfigProviderProps> = ({ value, children }) => (
  <AssistantConfigContext.Provider value={value}>{children}</AssistantConfigContext.Provider>
);

export const useAssistantConfig = () => useContext(AssistantConfigContext);
